import React, { useState } from 'react';
import { useSlider } from '@react-aria/slider';
import { useSliderState } from '@react-stately/slider';
import { useNumberFormatter } from '@react-aria/i18n';

import { getSliderNumberFormatConfig } from '@trqk/utils';

import { SliderBody } from './components';
import {
    defaultInputWidth,
    defaultMaxValue,
    defaultMinValue,
    defaultWidth,
} from './constants';

interface SliderProps {
    /**
     * Starting value for the slider. Since the Slider is build on @react-aria hooks that
     * support multiple thumbs, the value is expressed as an array of numbers; Slider currently
     * only supports a single value in this array (i.e. one thumb for the slider).
     */
    defaultValue: number[];
    /**
     * A label for the slider
     */
    label?: string;
    /**
     * Optional class config which will override the default border classes on the input
     */
    inputClassConfig?: InputClassConfig;
    /**
     * Optional CSS classes for the label on the input
     */
    inputLabelClasses?: string;
    /**
     * Width for the <input> element
     * @defaultValue 130
     */
    inputWidth?: number;
    /**
     * Slider's minimum value
     * @default 0
     */
    maxValue?: number;
    /**
     * Slider's maximum value
     * @default 100
     */
    minValue?: number;
    /**
     * How to format numbers for both the current value label and the min/max labels
     */
    numberFormat: SliderNumberFormat;
    /**
     * Method called with the next value when it changes
     */
    onChange: (value: number[]) => void;
    /**
     * The rounding type to use when a user enters a number that is not one of the allowed values.
     * See {@link https://github.com/trqk/trqk-client/blob/develop/packages/components/src/Slider/utils/getStepValue.ts | getStepValue}
     */
    roundType: SliderStepRoundType;
    /**
     * The slider's step value.
     * @default 1
     */
    step?: number;
    /**
     * Overall width; slider track will be smaller than this to allow for width of the thumb when
     * in leftmost or rightmost position
     * @default 250
     */
    width?: number;
}

/**
 * An accessible slider component built from React Aria hooks, based on this example:
 * {@link https://react-spectrum.adobe.com/react-aria/useSlider.html#useslider-hook}
 *
 * @param props - {@link SliderProps}
 */
export function Slider(props: SliderProps): JSX.Element {
    const {
        defaultValue,
        inputClassConfig,
        inputLabelClasses = 'pl-2',
        inputWidth = defaultInputWidth,
        label,
        maxValue = defaultMaxValue,
        minValue = defaultMinValue,
        numberFormat,
        roundType,
        step,
        width = defaultWidth,
    } = props;
    const [value, setValue] = useState(defaultValue);

    const onChange = (valueList: number[]): void => {
        setValue(valueList);
        props.onChange(valueList);
    };
    const trackRef = React.useRef(null);
    const { value: valueFormatOptions } =
        getSliderNumberFormatConfig(numberFormat);
    const valueFormatter = useNumberFormatter(valueFormatOptions);
    const state = useSliderState({
        defaultValue,
        label,
        maxValue,
        minValue,
        onChange,
        step,
        numberFormatter: valueFormatter,
        value,
    });

    const { groupProps, trackProps, labelProps, outputProps } = useSlider(
        {
            defaultValue,
            label,
            maxValue,
            minValue,
            step,
        },
        state,
        trackRef,
    );

    return (
        <SliderBody
            defaultValue={defaultValue}
            inputClassConfig={inputClassConfig}
            inputLabelClasses={inputLabelClasses}
            groupProps={groupProps}
            label={label}
            labelProps={labelProps}
            inputWidth={inputWidth}
            maxValue={maxValue}
            minValue={minValue}
            numberFormat={numberFormat}
            onChange={onChange}
            outputProps={outputProps}
            roundType={roundType}
            state={state}
            trackProps={trackProps}
            trackRef={trackRef}
            width={width}
        />
    );
}
